/**
 * Session-chat API (M6) — wraps gateway /api/sessions/:id/chat/messages.
 *
 * Two surfaces:
 *   1. Plain JSON: GET history, DELETE history.
 *   2. SSE streaming POST — exposed as an async generator of typed
 *      events. Consumers (session-chat-pane.tsx) read with for-await.
 *
 * The streaming side bypasses the apiJson wrapper because EventSource
 * doesn't support credentials cross-origin reliably; instead we fetch
 * with `accept: text/event-stream` and parse the body manually. Cookies
 * are forwarded via credentials: 'include' (same as apiFetch).
 */
import { apiFetch, apiJson } from './client';

export type ChatMessageType =
  | 'user_msg'
  | 'agent_msg'
  | 'tool_use'
  | 'tool_result'
  | 'thinking'
  | 'system_event';

export type ChatAuthorKind = 'user' | 'agent' | 'system';

/**
 * Artifacts touched this run (flag VITE_SESSION_ARTIFACTS_FOOTER). At run
 * finalize the gateway scans the card workspace and persists the files the run
 * created/updated onto the FINAL agent message's metadata as
 * `metadata.artifacts` (only present when non-empty, gated behind the gateway
 * SESSION_ARTIFACTS_FOOTER_ENABLED flag). `path` is relative to the card
 * workspace root (may be nested, e.g. `docs/report.md`); `name` is the
 * basename for display. Read from an agent row via
 * `(message.metadata?.artifacts as TouchedArtifact[] | undefined)`.
 */
export interface TouchedArtifact {
  path: string;
  name: string;
  action: 'created' | 'updated';
  size?: number;
  modifiedAt?: string;
}

export interface SessionChatMessage {
  id: string;
  sessionId: string;
  orgId: string;
  cardId: string | null;
  roleId: string | null;
  authorKind: ChatAuthorKind;
  authorUserId: string | null;
  messageType: ChatMessageType;
  body: string | null;
  toolUse: { id: string; name: string; input: unknown } | null;
  /**
   * Raw `session_messages.tool_result` jsonb, passed through verbatim by the
   * gateway (session-chat.ts:480). Only `tool_use_id` and `ok` are ever
   * written — see session-chat-interactive.ts:1604 / :3043 and
   * session-chat.ts:3038, all three of which persist
   * `{ tool_use_id, ok }`. Verified against production 2026-07-21:
   * of 17545 tool_result rows the top-level keys are `ok` (17545),
   * `tool_use_id` (17545) and `content_preview` (16709, legacy May rows).
   * `content` appears in ZERO rows.
   *
   * The three fields below were previously declared REQUIRED, which is how a
   * unit test came to fabricate `content` and pass against a shape the
   * gateway has never emitted. They are optional because they are absent.
   */
  toolResult: {
    tool_use_id: string;
    ok: boolean;
    status?: number;
    content?: string;
    content_preview?: string;
    is_error?: boolean;
  } | null;
  metadata: Record<string, unknown> | null;
  parentMessageId: string | null;
  /** M-X8: vendor that produced this turn (null for legacy rows). */
  vendor: 'anthropic' | 'openai' | 'google' | null;
  createdAt: string;
}

// Mirror of `claude --permission-mode` choices verified via
// `docker exec chaos-card-… claude --help` (2026-05-20). Server-side
// allowlist is the same set; anything outside falls back to 'default'.
export type ChatPermissionMode =
  | 'default'
  | 'acceptEdits'
  | 'auto'
  | 'dontAsk'
  | 'bypassPermissions'
  | 'plan';

// Mirror of `claude --effort` choices verified via
// `docker exec chaos-card-… claude --help` (2026-05-20). Server-side
// allowlist is the same set; anything outside falls back to 'medium'.
// 2026-06-09: 'max' is the honest top tier exposed in the picker
// (default). The former synthetic 'ultracode' relabel was dropped — the
// gateway still accepts it on the wire but the UI never offers/sends it.
// 2026-10-04: 'ultra' is OpenAI's own top level (codex 0.160.0). Which of
// these a picker offers is per vendor + model (lib/agent/effort-levels.ts).
export type ChatEffort = 'low' | 'medium' | 'high' | 'xhigh' | 'max' | 'ultracode' | 'ultra';

export interface ChatPostBody {
  body: string;
  model?: string;
  permission_mode?: ChatPermissionMode;
  effort?: ChatEffort;
  /** M-X8: per-message vendor override; defaults to session.vendor. */
  vendor?: 'anthropic' | 'openai' | 'google';
  /**
   * Session attachments (Phase 1, flag VITE_SESSION_ATTACHMENTS). Files the
   * founder attached to THIS message via the composer "+" — already uploaded to
   * the card KB store, so only the filenames travel. The gateway derives each
   * file's mounted path (/knowledge/card/<filename>) and hands it to the agent.
   * Ignored server-side when SESSION_ATTACHMENTS_ENABLED is OFF.
   */
  attachments?: Array<{ filename: string }>;
}

export type ChatStreamEvent =
  | { kind: 'text'; delta: string }
  | { kind: 'thinking'; delta: string }
  | { kind: 'tool_use'; id: string; name: string; input: unknown }
  | { kind: 'tool_result'; tool_use_id: string; ok: boolean; status: number; json: unknown }
  /**
   * A REFUSAL AND ITS WAY OUT (2026-08-06).
   *
   * `message` alone was all this carried, and the gateway's refusal bodies
   * have carried more than a message since 2026-08-02: `error` is a stable
   * code and `action` is an in-app route, put there "so the interface can
   * render a button instead of parsing English"
   * (EXECUTION_LANE_REFUSAL_ACTION, apps/chaos-gateway/src/org-auth.ts).
   * The client threw both away at the two places below, so a person told
   * "connect Claude or Codex in Settings → AI sign-ins" was handed a Retry
   * button and no way to reach that screen — and Retry cannot clear a refusal
   * that is waiting on a step nobody offered them.
   */
  | {
      kind: 'error';
      message: string;
      code?: string;
      action?: string;
      /** True only when an HTTP response or gateway SSE frame refused it. */
      authoritative?: boolean;
    }
  | { kind: 'done'; user_msg_id: string | null; agent_msg_id: string | null }
  // Conductor Phase 4 (SCHEDULER_ENABLED): instead of an SSE stream the send
  // route returns 202 JSON {queued:true, queue, reason, etaAt} when the
  // capacity gate blocks a fresh launch. We surface that as a synthetic
  // `queued` event (then a `done`) so the composer can render "Queued — will
  // start when capacity frees" rather than a streaming run. queueId/reason/
  // etaAt drive the composer notice + a queryClient invalidation.
  // FD-B / LD-15 (durable delivery, a DIFFERENT queue from the one above):
  // the gateway parked this prompt in the delivery ledger because the pane was
  // mid-turn. It is NOT a fault and NOT a refusal — no `error` follows, the run
  // row stays non-terminal, and the redelivery worker injects it in
  // `delivery_seq` order. The client's only job is to stop trusting its cached
  // runs payload: the authoritative queue state lives on the server, where
  // every participant of the card can read it.
  | {
      kind: 'delivery_queued';
      run_id: string;
      reason: string | null;
      user_msg_id: string | null;
    }
  | {
      kind: 'queued';
      queueId: string;
      reason:
        | 'max_concurrent'
        | 'ram'
        | 'cpu'
        | 'window_5h'
        | 'window_weekly'
        | 'window_exhausted'
        | null;
      etaAt: string | null;
    }
  // M-X11: Live Activity events.
  | {
      kind: 'run_started';
      run_id: string;
      container: string;
      user_msg_id: string | null;
    }
  | { kind: 'reattach_ready'; cursor: 'attached' }
  | { kind: 'reattach_unavailable'; reason: string }
  | {
      kind: 'run_progress';
      run_id: string;
      elapsedMs: number;
      tokensInput: number;
      tokensOutput: number;
      tokensCacheRead: number;
      tokensCacheCreation: number;
      /** Context-window OCCUPANCY level, re-stated on every heartbeat so a
       *  client that reattaches mid-turn learns it without waiting for the next
       *  API call. Absent while nothing has been measured. */
      contextTokens?: number;
      contextWindow?: number;
      subAgentsActive: number;
      subAgentsCompleted: number;
      toolCallsCount: number;
      currentActivity: string;
    }
  | {
      kind: 'tool_use_started';
      id: string;
      name: string;
      input_preview: string;
    }
  | {
      kind: 'tool_use_completed';
      tool_use_id: string;
      ok: boolean;
    }
  | {
      kind: 'subagent_started';
      parent_tool_use_id: string;
      subagent_type: string | null;
      description: string | null;
    }
  | {
      kind: 'subagent_completed';
      parent_tool_use_id: string;
      ok: boolean;
    }
  | {
      kind: 'usage';
      /** COST for the turn so far: totals summed across every API call. Right
       *  for the token counters in the activity dock, WRONG as a context-window
       *  reading (the same cached prompt is re-read, and so re-counted, once per
       *  call). */
      inputTokens?: number;
      outputTokens?: number;
      cacheReadTokens?: number;
      cacheCreationTokens?: number;
      /** OCCUPANCY: how full the model's context window is — a level measured
       *  at ONE API call, so it may fall after a /compact. Absent when the
       *  gateway had nothing to measure; the ring must then say so rather than
       *  fall back to a guess. */
      contextTokens?: number;
      /** Window reported by the vendor with this measured occupancy. */
      contextWindow?: number;
    };

/**
 * HTTP status alone never proves that this POST was refused before acceptance.
 * A bare proxy/service 5xx may arrive after the gateway committed the
 * user_msg/run, so it remains delivery-unknown. Any 4xx/5xx is authoritative
 * only when the gateway binds a terminal-prewrite receipt to this exact K;
 * that includes deliberate service-class refusals such as runtime-unavailable.
 */
export function isAuthoritativeChatHttpRefusal(
  status: number,
  refusal?: {
    terminalPrewrite?: boolean;
    clientIdempotencyKey?: string;
  } | null,
  expectedClientIdempotencyKey?: string,
): boolean {
  return (
    status >= 400 &&
    status < 600 &&
    refusal?.terminalPrewrite === true &&
    typeof expectedClientIdempotencyKey === 'string' &&
    refusal.clientIdempotencyKey === expectedClientIdempotencyKey
  );
}

/**
 * SESSION_LIVE_SYNC_V2 — live activity probe response.
 *
 * The gateway snapshots the claude TUI status line via a READ-ONLY
 * `tmux capture-pane` (never attaches/detaches the PTY) so the Session
 * view can feel live during a long single claude step. The endpoint is
 * gated behind the gateway's SESSION_LIVE_SYNC_V2 flag — when OFF it
 * returns `{ enabled:false }` and the UI no-ops. A Docker probe failure is
 * `containerLive:null`: inability to inspect is never reported as stopped.
 */
export interface SessionActivity {
  enabled: boolean;
  containerLive?: boolean | null;
  /** True when a claude spinner/status line is present and not idle. */
  working?: boolean;
  /** The gerund verb, e.g. "Architecting" (null if not parseable). */
  status?: string | null;
  /** Human elapsed clock, e.g. "11m 45s" (null if absent). */
  elapsed?: string | null;
  /** Compact token counter incl. direction arrow, e.g. "↓1.2k" (null if absent). */
  tokens?: string | null;
  /** Last ~18 cleaned, content-only rendered pane lines (chrome stripped). */
  lines?: string[];
  /** ISO timestamp of the capture. */
  capturedAt?: string;
}

export const sessionChatApi = {
  list(sessionId: string): Promise<SessionChatMessage[]> {
    return apiJson<{ messages: SessionChatMessage[] }>(
      `/sessions/${encodeURIComponent(sessionId)}/chat/messages`,
    ).then((r) => r.messages);
  },
  /**
   * Live activity snapshot for the Session view. Strictly defensive:
   * resolves to `{ enabled:false }` on ANY error so callers can render
   * nothing without try/catch at the call site.
   */
  async activity(sessionId: string): Promise<SessionActivity> {
    try {
      const r = await apiJson<SessionActivity>(
        `/sessions/${encodeURIComponent(sessionId)}/activity`,
      );
      return r && typeof r === 'object' ? r : { enabled: false };
    } catch {
      return { enabled: false };
    }
  },
  clear(sessionId: string, principalFence: string): Promise<void> {
    return apiJson<void>(`/sessions/${encodeURIComponent(sessionId)}/chat/messages`, {
      method: 'DELETE',
      headers: { 'X-Chaos-Expected-Principal': principalFence },
    });
  },
  restoreHistory(
    sessionId: string,
    resetId: string,
    principalFence: string,
  ): Promise<{ ok?: boolean; restoredCount?: number }> {
    return apiJson<{ ok?: boolean; restoredCount?: number }>(
      `/sessions/${encodeURIComponent(sessionId)}/history/${encodeURIComponent(resetId)}/restore`,
      {
        method: 'POST',
        headers: { 'X-Chaos-Expected-Principal': principalFence },
      },
    );
  },
  /**
   * Stream a new message → yields events as they arrive. The generator
   * completes when the server sends `event: done` or the socket closes.
   *
   * Yields parsed SSE events. Caller must consume to completion (e.g.
   * `for await (const evt of …)`).
   */
  async *stream(
    sessionId: string,
    body: ChatPostBody,
    options: {
      signal?: AbortSignal;
      /** Durable identity for one logical send; the `send:` namespace is added here. */
      idempotencyKey: string;
      /** Fresh, memory-only cookie principal proof. Unsafe calls never infer it globally. */
      principalFence: string;
    },
  ): AsyncGenerator<ChatStreamEvent, void, void> {
    const { signal, idempotencyKey, principalFence } = options;
    const resp = await apiFetch(
      `/sessions/${encodeURIComponent(sessionId)}/chat/messages`,
      {
        method: 'POST',
        json: body,
        headers: {
          Accept: 'text/event-stream',
          'x-chaos-idempotency-key': `send:${idempotencyKey}`,
          'X-Chaos-Expected-Principal': principalFence,
        },
        signal,
      },
    );
    yield* readChatResponse(resp, idempotencyKey);
  },
  /**
   * WP-4 (CONTINUOUS_SESSIONS_SPEC_v1 §5.4): a step through the card's step
   * route, `POST /api/cards/:cardId/steps` — the gateway resolves the viewer's
   * own CURRENT session for the vendor (adopting a pre-flag row, rung 0) and
   * runs the step through that session's chat pipeline, so the answer is the
   * SAME stream `stream()` reads. `onStepSession` hears which session the
   * gateway resolved (`x-chaos-step-session-id`) before the first event, so
   * the pane can follow it if it is not the one on screen. Used only while
   * the card is on the multi-user model.
   */
  async *streamStep(
    cardId: string,
    body: ChatPostBody,
    options: {
      signal?: AbortSignal;
      idempotencyKey: string;
      principalFence: string;
      onStepSession?: (step: { sessionId: string; vendor: string | null; created: boolean }) => void;
    },
  ): AsyncGenerator<ChatStreamEvent, void, void> {
    const { signal, idempotencyKey, principalFence } = options;
    const resp = await apiFetch(`/cards/${encodeURIComponent(cardId)}/steps`, {
      method: 'POST',
      json: body,
      headers: {
        Accept: 'text/event-stream',
        'x-chaos-idempotency-key': `send:${idempotencyKey}`,
        'X-Chaos-Expected-Principal': principalFence,
      },
      signal,
    });
    const stepSessionId = resp.headers.get('x-chaos-step-session-id');
    if (stepSessionId && options.onStepSession) {
      options.onStepSession({
        sessionId: stepSessionId,
        vendor: resp.headers.get('x-chaos-step-vendor'),
        created: resp.headers.get('x-chaos-step-session-created') === '1',
      });
    }
    yield* readChatResponse(resp, idempotencyKey);
  },
};

/**
 * The answer of the chat send route — a refusal, a 202 queue placement or the
 * SSE stream — as events. Shared by `stream()` and `streamStep()` (the step
 * route answers with the chat route's own response).
 */
async function* readChatResponse(
  resp: Response,
  idempotencyKey: string,
): AsyncGenerator<ChatStreamEvent, void, void> {
  if (!resp.ok) {
    let parsed: {
      error?: string;
      message?: string;
      action?: string;
      refusal?: {
        terminalPrewrite?: boolean;
        clientIdempotencyKey?: string;
      };
    } = {};
    try {
      parsed = await resp.json();
    } catch {}
    yield {
      kind: 'error',
      message: parsed.message ?? parsed.error ?? `http_${resp.status}`,
      // Carry the refusal's code and its way out, when the gateway sent
      // them. `action` is an in-app route (never prose, never a URL from
      // elsewhere); the pane renders it as a link on the failed bubble.
      ...(typeof parsed.error === 'string' ? { code: parsed.error } : {}),
      ...(typeof parsed.action === 'string' ? { action: parsed.action } : {}),
      authoritative: isAuthoritativeChatHttpRefusal(
        resp.status,
        parsed.refusal,
        idempotencyKey,
      ),
    };
    yield { kind: 'done', user_msg_id: null, agent_msg_id: null };
    return;
  }

  // ── Conductor Phase 4: run-now-or-queue (SCHEDULER_ENABLED) ─────────
  // The send route returns 202 application/json {queued:true, queue, reason,
  // etaAt} when the capacity gate blocked this fresh launch — NOT an SSE
  // stream. Detect it by status (202) before we try to read the body as a
  // stream, and translate it into a synthetic `queued` + `done` so the
  // consumer never blocks on a non-streaming body. (When SCHEDULER_ENABLED
  // is OFF server-side this branch is never taken — the route always streams,
  // byte-identical to today.)
  if (resp.status === 202) {
    let parsed: {
      queued?: boolean;
      queue?: { id?: string; blockedReason?: string | null; etaAt?: string | null };
      reason?: string | null;
      etaAt?: string | null;
    } = {};
    try {
      parsed = await resp.json();
    } catch {}
    if (parsed.queued) {
      const reason = (parsed.reason ?? parsed.queue?.blockedReason ?? null) as Extract<
        ChatStreamEvent,
        { kind: 'queued' }
      >['reason'];
      yield {
        kind: 'queued',
        queueId: String(parsed.queue?.id ?? ''),
        reason,
        etaAt: parsed.etaAt ?? parsed.queue?.etaAt ?? null,
      };
      yield { kind: 'done', user_msg_id: null, agent_msg_id: null };
      return;
    }
    // 202 without queued:true → degrade to a clean done (no error toast).
    yield { kind: 'done', user_msg_id: null, agent_msg_id: null };
    return;
  }

  if (!resp.body) {
    yield { kind: 'error', message: 'no_response_body' };
    yield { kind: 'done', user_msg_id: null, agent_msg_id: null };
    return;
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';
  let currentEvent: string | null = null;
  let currentData: string[] = [];

  const drain = (): ChatStreamEvent[] => {
    if (!currentEvent || currentData.length === 0) return [];
    const evtName = currentEvent;
    const raw = currentData.join('\n');
    currentEvent = null;
    currentData = [];
    let parsed: any;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return [{ kind: 'error', message: `bad_sse_payload: ${raw.slice(0, 200)}` }];
    }
    switch (evtName) {
      case 'text':
        return [{ kind: 'text', delta: String(parsed.delta ?? '') }];
      case 'thinking':
        return [{ kind: 'thinking', delta: String(parsed.delta ?? '') }];
      case 'tool_use':
        return [
          {
            kind: 'tool_use',
            id: String(parsed.id),
            name: String(parsed.name),
            input: parsed.input,
          },
        ];
      case 'tool_result':
        return [
          {
            kind: 'tool_result',
            tool_use_id: String(parsed.tool_use_id),
            ok: !!parsed.ok,
            status: Number(parsed.status ?? 0),
            json: parsed.json,
          },
        ];
      case 'error':
        return [
          {
            kind: 'error',
            message: String(parsed.message ?? 'error'),
            ...(typeof parsed.error === 'string' ? { code: parsed.error } : {}),
            ...(typeof parsed.action === 'string' ? { action: parsed.action } : {}),
            authoritative:
              parsed.terminal_prewrite === true &&
              typeof idempotencyKey === 'string' &&
              parsed.client_idempotency_key === idempotencyKey,
          },
        ];
      case 'delivery_queued':
        return [
          {
            kind: 'delivery_queued',
            run_id: String(parsed.run_id ?? ''),
            reason: typeof parsed.reason === 'string' ? parsed.reason : null,
            user_msg_id:
              typeof parsed.user_msg_id === 'string' ? parsed.user_msg_id : null,
          },
        ];
      case 'done':
        return [
          {
            kind: 'done',
            user_msg_id: parsed.user_msg_id ?? null,
            agent_msg_id: parsed.agent_msg_id ?? null,
          },
        ];
      // M-X11: Live Activity events.
      case 'run_started':
        return [
          {
            kind: 'run_started',
            run_id: String(parsed.run_id ?? ''),
            container: String(parsed.container ?? ''),
            user_msg_id:
              typeof parsed.user_msg_id === 'string' ? parsed.user_msg_id : null,
          },
        ];
      case 'reattach_ready':
        return [{ kind: 'reattach_ready', cursor: 'attached' }];
      case 'reattach_unavailable':
        return [
          {
            kind: 'reattach_unavailable',
            reason: String(parsed.reason ?? 'cursor_unavailable'),
          },
        ];
      case 'run_progress':
        return [
          {
            kind: 'run_progress',
            run_id: String(parsed.run_id ?? ''),
            elapsedMs: Number(parsed.elapsedMs ?? 0),
            tokensInput: Number(parsed.tokensInput ?? 0),
            tokensOutput: Number(parsed.tokensOutput ?? 0),
            tokensCacheRead: Number(parsed.tokensCacheRead ?? 0),
            tokensCacheCreation: Number(parsed.tokensCacheCreation ?? 0),
            contextTokens:
              typeof parsed.contextTokens === 'number' ? parsed.contextTokens : undefined,
            contextWindow:
              typeof parsed.contextWindow === 'number'
                && Number.isSafeInteger(parsed.contextWindow)
                && parsed.contextWindow > 0
                ? parsed.contextWindow : undefined,
            subAgentsActive: Number(parsed.subAgentsActive ?? 0),
            subAgentsCompleted: Number(parsed.subAgentsCompleted ?? 0),
            toolCallsCount: Number(parsed.toolCallsCount ?? 0),
            currentActivity: String(parsed.currentActivity ?? ''),
          },
        ];
      case 'tool_use_started':
        return [
          {
            kind: 'tool_use_started',
            id: String(parsed.id ?? ''),
            name: String(parsed.name ?? 'unknown'),
            input_preview: String(parsed.input_preview ?? ''),
          },
        ];
      case 'tool_use_completed':
        return [
          {
            kind: 'tool_use_completed',
            tool_use_id: String(parsed.tool_use_id ?? ''),
            ok: !!parsed.ok,
          },
        ];
      case 'subagent_started':
        return [
          {
            kind: 'subagent_started',
            parent_tool_use_id: String(parsed.parent_tool_use_id ?? ''),
            subagent_type:
              typeof parsed.subagent_type === 'string' ? parsed.subagent_type : null,
            description:
              typeof parsed.description === 'string' ? parsed.description : null,
          },
        ];
      case 'subagent_completed':
        return [
          {
            kind: 'subagent_completed',
            parent_tool_use_id: String(parsed.parent_tool_use_id ?? ''),
            ok: !!parsed.ok,
          },
        ];
      case 'usage':
        return [
          {
            kind: 'usage',
            inputTokens:
              typeof parsed.inputTokens === 'number' ? parsed.inputTokens : undefined,
            outputTokens:
              typeof parsed.outputTokens === 'number' ? parsed.outputTokens : undefined,
            cacheReadTokens:
              typeof parsed.cacheReadTokens === 'number' ? parsed.cacheReadTokens : undefined,
            cacheCreationTokens:
              typeof parsed.cacheCreationTokens === 'number'
                ? parsed.cacheCreationTokens
                : undefined,
            // Deliberately NOT defaulted to 0: absent means "not measured",
            // and a 0 here would render as an empty context window.
            contextTokens:
              typeof parsed.contextTokens === 'number' ? parsed.contextTokens : undefined,
            contextWindow:
              typeof parsed.contextWindow === 'number'
                && Number.isSafeInteger(parsed.contextWindow)
                && parsed.contextWindow > 0
                ? parsed.contextWindow : undefined,
          },
        ];
      default:
        return [];
    }
  };

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';
    for (const line of lines) {
      const trimmed = line.replace(/\r$/, '');
      if (trimmed === '') {
        for (const evt of drain()) yield evt;
        continue;
      }
      if (trimmed.startsWith(':')) continue; // SSE comment
      if (trimmed.startsWith('event:')) {
        currentEvent = trimmed.slice(6).trim();
      } else if (trimmed.startsWith('data:')) {
        currentData.push(trimmed.slice(5).replace(/^ /, ''));
      }
    }
  }
  // Flush trailing buffer.
  if (buffer) {
    const trimmed = buffer.replace(/\r$/, '');
    if (trimmed.startsWith('event:')) currentEvent = trimmed.slice(6).trim();
    else if (trimmed.startsWith('data:')) currentData.push(trimmed.slice(5).replace(/^ /, ''));
  }
  for (const evt of drain()) yield evt;
}

export const sessionChatKeys = {
  all: ['session-chat'] as const,
  forSession: (sessionId: string) => [...sessionChatKeys.all, 'session', sessionId] as const,
};
